/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import _ from "underscore";
import util from "../util/util.js";
import fieldManager from "../util/fields.js";

var program = {
	code: "bnb",
	name: "Beach-nesting Birds",
	description: "<p>The Beach-nesting Birds project aims to recover and build resilience of populations of species (plovers, oystercatchers, terns and stone-curlews) which nest on beaches, islands, lakes and wetlands.</p>" +
	"<p>Data is the key to understanding this suite of birds which often occupy habitats that are heavily used by people (plus their dogs, vehicles, horses) or are at major threat of development or modification. Beach-nesting birds have well camouflaged nests and chicks, and passive nest defence behaviours that make them highly prone to disturbance. Many of these species are listed as threatened either in particular states or at a national level.</p>" +
	"<p>The species of focus are: Hooded Plover, Pied Oystercatcher, Beach Stone-curlew, Red-capped Plover, Sooty Oystercatcher and terns, in particular, Fairy Tern, Little Tern, Caspian Tern and Crested Tern.</p>" +
	"<p>There are two main types of monitoring:</p>" +
	"<p><strong>1. Population Monitoring:</strong> Surveys of a beach, wetland or lake to monitor the occurrence and exact location of these shorebirds or terns. This includes the <strong>Hooded Plover Biennial Count.</strong></p><p>This data is very useful for estimating population size, recording precise locations of breeding pairs, understanding occupancy of sites over time and for identifying important habitat. Surveys include threat data so that we learn more about threats to the birds at these locations and across their range. Click 'Next' to continue this data form.</p>" +
	"<p><strong>2. Breeding success monitoring: </strong> Repeated visits to a nesting pair during the breeding season to document outcomes of their breeding attempts and collect threat data to inform on-ground adaptive management.</p><p>This is highly sensitive data and only trained volunteers should be collecting this information due to the potential risks of disturbing nesting birds. You can sign up for training and become a nest monitor/citizen scientist, and you will then be given access to our restricted portal: <a href='https://portal.mybeachbird.com.au/'>https://portal.mybeachbird.com.au</a></p>Contact: <a href='mailto:beachnestingbirds@birdlife.org.au'>beachnestingbirds@birdlife.org.au</a>",
	load: function() {
		return birdata.lookup().then(function(lookups) {
			program.options = lookups;
			return true;
		});
	},
	programData: function(survey) {
		var fields = program.surveyFields();

		var speciesIDs = [
			112, // Caspian Tern
			113, // Roseate Tern
			115, // Crested Tern
			116, // Lesser Crested Tern
			117, // Little Tern
			118, // Fairy Tern
			119, // Black-naped Tern
			130, // Australian Pied Oystercatcher
			131, // Sooty Oystercatcher
			138, // Hooded Plover
			143, // Red-capped Plover
			174, // Bush Stone-curlew
			175  // Beach Stone-curlew
		];

		var speciesList = ko.observable([]);
		birdata.speciesList().then(function(spp) { // Note this causes warning about promise not being returned
			speciesList(spp.filter(function(sp) { return speciesIDs.includes(sp.id); }));
		});

		return {
			fields: fields,
			validations: _.values(fields)
			// speciesList: speciesList
		};
	},
	fields: function() {
		return program.surveyFields();
	},
	surveyFields: function() {
		var fields = [
			{
				name: "additionalObservers",
				type: "text",
				label: "Additional observers"
			},
			{
				name: "partOfBiennialHPCount",
				type: "boolean",
				label: "Part of Hooded Plover biennial count"
			},
			{
				name: "surveyMeans",
				type: "options",
				label: "Survey conducted by",
				options: program.options.s2020_survey_means,
				validate: "required"
			},
			{
				name: "temperatureCelsius",
				type: "number",
				label: "Temperature",
				units: "°C",
				max: 80,
				min: -20,
				decimalPlaces: 1 // TODO: enforce this in number field
			},
			{
				name: "windSpeed",
				type: "options",
				label: "Wind speed",
				options: program.options.wind_speed
			},
			{
				name: "tide",
				type: "options",
				label: "Tide",
				options: program.options.s2020_tide
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	sightingFields: function(sightingModel) {
		var species = sightingModel ? sightingModel.species : null;

		// To avoid null issues
		function nestingCodeIsOneOf(/*args*/) {
			var nesting = fields.nesting();
			return _.contains(arguments, nesting && nesting.code);
		};

		var fields = [
			{
				name: "adultCount",
				type: "integer",
				label: "Adults",
				max: 99999,
				validate: "required"
			},
			{
				name: "juvenileCount",
				type: "integer",
				label: "Juveniles",
				max: 99999
			},
			{
				name: "habitat",
				type: "options",
				label: "Habitat",
				options: program.options.bnb_habitat,
				validate: "required"
			},
			{
				name: "nesting",
				type: "options",
				label: "Breeding activity",
				options: program.options.bnb_nesting,
				validate: "required"
			},
			{
				// Visible if nesting = nest_with_eggs
				name: "eggCount",
				type: "integer",
				label: "Number of eggs",
				min: 1,
				max: 9999,
				help: "Leave blank if number of eggs not checked",
				hidden: () => !nestingCodeIsOneOf("nest_with_eggs")
			},
			{
				// Visible if nesting = nest_with_eggs,scrape_no_eggs
				name: "nestHabitat",
				type: "options",
				label: "Habitat of nest location",
				options: program.options.bnb_habitat,
				hidden: () => !nestingCodeIsOneOf("scrape_no_eggs", "nest_with_eggs")
			},
			{
				// Visible if nesting = chicks_sighted,colony_with_chicks,colony_with_nests_and_chicks
				name: "chickCount",
				type: "integer",
				label: "Number of chicks",
				min: 1,
				max: 9999,
				hidden: () => !nestingCodeIsOneOf("chicks_sighted", "colony_with_chicks", "colony_with_nests_and_chicks")
			},
			{
				// Visible if nesting = chicks_sighted,colony_with_chicks,colony_with_nests_and_chicks
				name: "chickSize",
				type: "options",
				label: "Size/age of chicks",
				options: program.options.bnb_chick_size,
				hidden: () => !nestingCodeIsOneOf("chicks_sighted", "colony_with_chicks", "colony_with_nests_and_chicks")
			},
			{
				// Visible if nesting = chicks_sighted
				name: "chickBehaviour",
				type: "options",
				label: "Behaviour of chicks",
				options: program.options.bnb_chick_behaviour,
				hidden: () => !nestingCodeIsOneOf("chicks_sighted")
			},
			{
				// Visible if nesting = colony_with_nests,colony_with_nests_and_chicks
				name: "incubatingCount",
				type: "integer",
				label: "Number of birds confirmed sitting/incubating",
				min: 1,
				max: 9999,
				hidden: () => !nestingCodeIsOneOf("colony_with_nests", "colony_with_nests_and_chicks")
			},
			{
				name: "colonyHabitat",
				type: "options",
				label: "Habitat of nest colony location",
				options: program.options.bnb_habitat,
				hidden: () => !nestingCodeIsOneOf("colony_with_nests", "colony_with_nests_and_chicks")
			},
			{
				name: "adultBehaviour",
				type: "options",
				label: "Behaviour of adults",
				options: program.options.bnb_adult_behaviour,
				hidden: () => !nestingCodeIsOneOf("suspect_nest", "suspect_chicks")
			},
			{
				name: "tags",
				type: "multi-text",
				label: "Bands or flags",
				help: "e.g. PE Right (White); Red/Metal Left, Yellow/Orange Right; orange Right unknown ID",
				addLabel: "Add bands/flags for an individual"
			},
			{
				name: "threats",
				type: "compound",
				minCount: 0,
				maxCount: 1,
				fields: [
					{
						name: "accessWithinView",
						type: "boolean"
					},
					{
						name: "humanCount",
						type: "integer",
						label: "# People",
						defaultValue: "0",
						min: 0,
						max: 9999
					},
					{
						name: "humanPrintsAboveHighTide",
						type: "boolean",
						label: "Human prints above high tide mark"
					},
					{
						name: "duneUse",
						type: "boolean",
					},
					{
						name: "fourWheelDriveEvidence",
						type: "boolean",
						label: "4WDs"
					},
					{
						name: "trailBikeEvidence",
						type: "boolean",
						label: "Trail bikes"
					},
					{
						name: "quadBikeEvidence",
						type: "boolean",
						label: "Quad bikes"
					},
					{
						name: "dogsOnLeashCount",
						type: "integer",
						label: "# Dogs on leash",
						defaultValue: "0",
						min: 0,
						max: 999
					},
					{
						name: "dogsOffLeashCount",
						type: "integer",
						label: "# Dogs off leash",
						defaultValue: "0",
						min: 0,
						max: 999
					},
					{
						name: "dogPrints",
						type: "boolean",
					},
					{
						name: "foxEvidence",
						type: "boolean",
						label: "Foxes"
					},
					{
						name: "catEvidence",
						type: "boolean",
						label: "Cats"
					},
					{
						name: "stockEvidence",
						type: "boolean",
						label: "Stock"
					},
					{
						name: "horseEvidence",
						type: "boolean",
						label: "Horses"
					},
					{
						name: "pigEvidence",
						type: "boolean",
						label: "Pigs"
					},
					{
						name: "deerEvidence",
						type: "boolean",
						label: "Deer"
					},
					{
						name: "magpieCount",
						type: "integer",
						label: "# Magpies",
						defaultValue: "0",
						min: 0,
						max: 999
					},
					{
						name: "ravenCount",
						type: "integer",
						label: "# Ravens",
						defaultValue: "0",
						min: 0,
						max: 999
					},
					{
						name: "otherThreats",
						type: "text",
						multiline: true,
						label: "Additional threats"
					},
					{
						name: "duneVegetationDensity",
						type: "options",
						label: "Vegetation density",
						options: program.options.vegetation_density,
						validate: "required",
						defaultValue: _.findWhere(program.options.vegetation_density, { code: "none" })
					},
					{
						name: "marramGrassPresent",
						type: "boolean",
						label: "Marram grass"
					},
					{
						name: "seaSpurgePresent",
						type: "boolean",
						label: "Sea spurge"
					},
					{
						name: "seaWheatGrassPresent",
						type: "boolean",
						label: "Sea wheat-grass"
					},
					{
						name: "beachDaisyPresent",
						type: "boolean",
						label: "Beach daisy"
					},
					{
						name: "pypGrassPresent",
						type: "boolean",
						label: "Pyp grass"
					},
					{
						name: "unknownWeedPresent",
						type: "boolean",
						label: "Unknown weed"
					},
					{
						name: "otherWeeds",
						type: "text"
					}
				]
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().bnb.fields;
		json.bnb = {};
		var route = survey.routeInfo();
		if(route) {
			if(route.sharedSite) {
				json.bnb.shared_site_id = route.sharedSite.id;
				json.bnb.partial_route = route.partialRoute;
			}
			if(route.start) {
				json.bnb.start_lon = route.start.lon;
				json.bnb.start_lat = route.start.lat;
			}
			if(route.finish) {
				json.bnb.finish_lon = route.finish.lon;
				json.bnb.finish_lat = route.finish.lat;
			}
		}
		json.all_species_recorded = false;
		_.each(json.sightings, function(sighting) {
			sighting.count = (+sighting.bnb.adult_count || 0) + (+sighting.bnb.juvenile_count || 0);
		});

		_.each(fields, function(f) {
			f.field.save(json.bnb);
		});
	},
	hideWaterLevel: true,
	hideSurveyType: true,
	surveyTypeOptions: [],
	afterSelect: function(survey) {
		// Set survey type after program selected
		survey.type({
			code: 'fixed_route_search',
			name: 'Fixed route search'
		});
		survey.allSpeciesRecorded(false);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.bnb;

		if(!data) return; //incomplete survey

		_.each(fields, function(f) {
			f.field.load(data);
		});
	},
	visibleSharedSiteLayers: () => ["bnb"],
	defaultAreaLayer: (layers) => null,
	updateMap: function(map, survey, areasPromise, locationViewModel) {
	},
	routeBasedEntry: true,
	existingRoutesDefinedBySharedSites: true,
	multipleSightingsPerSpecies: true,
	sightingLocations: true,
	forceSightingLocations: true,
	sightingNotesPlaceholder: 'Additional notes not captured elsewhere',
	allSpeciesRecordedEnabled: function() { return false; },
	multipleDaysEnabled: function() { return false; },
	observerCountEnabled: function() { return true; },
	forcePrivate: true
};

export default program;
